import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import moment from "moment";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ListItemIcon, Stack, Typography, Grid, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MessageBox from "../../../../util/messageBox";
import useOpenState from "../../../../hooks/useOpenState";
import { barsContextActive } from "../../../../store/reducers/reviews";
import { useDispatch, useSelector } from "react-redux";
import { calculatePatientAge } from "../../../../util/patientUtil";
import { getPatientName } from "../../../bedManagement/bmUtils";
import {
  calculateBarsPatientAge,
  getBarsAppointmentReason,
  getBarsPatientName,
} from "../utils/barsUtils";
import BarsTimer from "./barsTimer";
import { AppointmentSlotStatus } from "../utils/slotUtils";
import StatusIndicator from "../list/appointmentStatus";
import {
  openAppointmentModal,
  sessionsState,
  updateAppointmentStatus,
} from '../../../../store/reducers/appointments';
import { SLOT_STATUSES } from "../../utils/constants/appointmentSessionConstants";
import TruncatedText from "../../../../components/ui/text/truncatedText";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {APPOINTMENT_MODALS} from '../../utils/constants/appointmentSessionConstants';
import {useHistory} from 'react-router-dom';
import {getConfig} from '../../../../util/configUtil';
import HistoryIcon from '@mui/icons-material/History';
import { sendUserAudit } from "../../../../store/reducers/user";
import { AUDIT_TYPES } from "../../../../util/auditUtil";

const useStyles = makeStyles(() => ({
  ptContextRoot: {
    maxWidth: "100%",
    flexBasis: "100%",
    // padding: '2px 6px',
    // alignItems: "center",
    alignItems: "flex-start",
    alignContent: "flex-start",
    // justifyContent: "center",
    flexWrap: "nowrap",
  },
  dataRow: {
    gap: 5,
    fontSize: ".9rem",
    alignItems: "center",
    flexBasis: "fit-content",

    "&.firstRow": {
      position: "relative",
    },
    "&.lastRow": {
      flexGrow: 1,
      display: "flex",
      flexBasis: "100%",
      alignItems: "center",
      marginBottom: "0 !important",
      justifyContent: "space-between",
    },
  },
  firstLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  gender: {
    right: -45,
    bottom: -7,
    display: "flex",
    marginLeft: 1,
    marginRight: 8,
    color: "#222425",
    borderRadius: 6,
    transform: "scale(.55)",
    justifyContent: "center",

    "&.male": {
      backgroundColor: "#92bfe2",
    },
    "&.female": {
      backgroundColor: "#f4bdd1",
    },
  },
  textRow: {
    overflow: "hidden",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  menuItem: {
    fontSize: "small",
    width: "100%",
    minWidth: 100,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: 'center',

    "& svg, p*": {
      color: 'red'
    }
  },
  barsTimer: {
    gap: 0,
    alignItems: "flex-end",

    "& .MuiTypography-root": {
      fontWeight: 500,
      minWidth: "fit-content",
    },
  },
  patientName: {
    "&:hover": {
      opacity: .5,
      cursor: 'pointer',
    }
  }
}));

const WidgetPatientToBeSeenContext = ({
  ptInWard,
  calendarView
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const sessions = useSelector(sessionsState);
  const barsCtxActive = useSelector(barsContextActive);

  const [isModalOpen, openModal, closeModal] = useOpenState(false, true);

  const [menuEl, setMenuEl] = useState(null);
  const open = Boolean(menuEl);

  const disableActionsEnabled = getConfig('modules.scheduler.disableActionsAfterNow', false);
  const disableActionsMinutes = getConfig('modules.scheduler.disableActionsAfterMinutes', 5);

  const canCancelSlot = disableActionsEnabled ? moment(ptInWard?.startDate).isAfter(moment().subtract(disableActionsMinutes, 'minute')) : true;

  // CONTEXT-BASED PATIENT DATA
  const patientName = barsCtxActive
    ? getBarsPatientName(ptInWard)
    : getPatientName(ptInWard);

  const patientAge = barsCtxActive
    ? calculateBarsPatientAge(ptInWard)
    : calculatePatientAge(ptInWard);

  const appointmentReason = barsCtxActive
    ? getBarsAppointmentReason(ptInWard)
    : ptInWard?.description || "-";


  const handleOpenMenu = (event) => {
    setMenuEl(event.currentTarget);
  };

  const handleCloseMenu = (e) => {
    const tag = e?.target?.tagName?.toLowerCase();
    if (tag !== "input" && tag !== "svg") {
      setMenuEl(null);
    }
  };

  const handleOpenPatient = () => {
    dispatch(sendUserAudit({patient: ptInWard?.patient}, AUDIT_TYPES.OPEN_PATIENT_CONTEXT));
    history.push('/patient-context/' + ptInWard.patient.__id);
  }

  const confirmationModalMessage = useMemo(() => {
      const appointmentDateString = moment(ptInWard?.startDate).format(
        "Do MMM h:mma",
      );

      return `Are you sure you want to cancel ${getPatientName(ptInWard)}'s appointment on ${appointmentDateString}?`;
  }, [ptInWard]);

  const onAcceptConfirmation = useCallback(() => {
    dispatch(
      updateAppointmentStatus(
        ptInWard?.__id,
        AppointmentSlotStatus.DELETED,
        closeModal,
        closeModal,
      ),
    );
  }, [ptInWard]);

  const handleAppStatusChange = (newStatus) => {
    if (ptInWard.__id && newStatus?.value) {
      if (newStatus.value !== SLOT_STATUSES.ARRIVED.value) {
        dispatch(updateAppointmentStatus(ptInWard.__id, newStatus.value));
      } else {
        dispatch(
            openAppointmentModal(APPOINTMENT_MODALS.PATIENT_REGISTRY, {...ptInWard}),
        );
      }
    }
  };

  const onHistoryButtonClick = () => {
    dispatch(
        openAppointmentModal(APPOINTMENT_MODALS.STATUS_TIMELINE, { data: ptInWard }),
    );
  }

  return !ptInWard ? (
    <Typography
      style={{ display: "block", marginBottom: "auto" }}
      variant="subtitle2"
    >
      {t("No patient data available.")}
    </Typography>
  ) : (
    <>
      <Grid
        container
        className={clsx(classes.ptContextRoot, "scroll-normal-white")}
      >
        <Grid
          container
          item
          gap={1}
          className={classes.firstLine}
          style={{ height: "100%", alignContent: "flex-start", flexGrow: 1 }}
        >
          <Stack direction='row' sx={{p:0, m:0}} onClick={handleOpenPatient} className={classes.patientName}>
            <TruncatedText
              fontSize=".8rem"
              fontWeight={600}
              text={patientName}
              variant="subtitle2"
            />
          </Stack>

          <Stack gap={1} direction="row" flexBasis="100%" flexWrap="nowrap">
            <Stack gap={1} minWidth="fit-content" maxWidth={150} flexShrink={0}>
              <TruncatedText text="Hospital No." />
              <TruncatedText text="Age:" />
              <TruncatedText text="Reason:" />
            </Stack>

            <Stack gap={1} flexGrow={1} maxWidth={200}>
              <TruncatedText text={ptInWard?.patient?.hospitalNumber || "-"} />
              <TruncatedText text={patientAge} />
              <TruncatedText text={ptInWard?.description} />
            </Stack>
          </Stack>
        </Grid>

        <Grid
          item
          container
          className={clsx(classes.dataRow, "firstRow")}
          justifyContent="flex-end"
          style={{
            height: "100%",
            alignContent: "space-between",
            // flexBasis: "105px",
            flexBasis: "fit-content",
            flexShrink: 0,
          }}
        >
          {/*<Grid item xs={12}>*/}
          <Grid item>
            <Stack alignItems="flex-end">
              <StatusIndicator
                patient={ptInWard}
                status={ptInWard?.status}
                handleStatusChange={handleAppStatusChange}
              />

              <BarsTimer
                targetTime={moment(ptInWard.startDate).toISOString()}
                boxDirection="column"
                showDate={false}
                rootClasses={clsx(classes.barsTimer)}
              />
            </Stack>
          </Grid>

          {(!barsCtxActive || canCancelSlot) && (
              <Grid item container xs={12} justifyContent="flex-end">
                <IconButton size="small" onClick={handleOpenMenu} style={{ height: 25, width: 25, marginTop: "auto" }}>
                  <MoreVertIcon />
                </IconButton>
              </Grid>
          )}
        </Grid>
      </Grid>

      <Menu
        anchorEl={menuEl}
        id="ward-panel-menu"
        open={open}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            bgcolor: "background.paperDarker",

            p: 0.5,
            borderRadius: "10px",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -1.5,
              mr: 1,
            },
          },
        }}
        BackdropProps={{
          invisible: true,
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* TEMPORARY HIDDEN! */}
        {/*<MenuItem*/}
        {/*  className={classes.menuItem}*/}
        {/*  disableRipple*/}
        {/*  onClick={onPatientReAssign}*/}
        {/*>*/}
        {/*  <ListItemIcon>*/}
        {/*    <AgyleNamedIcon name="reschedule"/>*/}
        {/*  </ListItemIcon>*/}

        {/*  <Typography variant='body2'>*/}
        {/*    {t("Reschedule")}*/}
        {/*  </Typography>*/}
        {/*</MenuItem>*/}

        {!barsCtxActive && (
            <MenuItem
                className={classes.menuItem}
                disableRipple
                disabled={false}
                onClick={onHistoryButtonClick}
                // disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT))}
            >
              <ListItemIcon>
                <HistoryIcon />
              </ListItemIcon>

              <Typography variant='body2'>
                {t("Status Timeline")}
              </Typography>
            </MenuItem>
        )}

        {calendarView && !barsCtxActive && canCancelSlot && (
            <MenuItem
                className={classes.menuItem}
                disableRipple
                disabled={false}
                onClick={openModal}
                // disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT))}
            >
              <ListItemIcon>
                <CancelOutlinedIcon color='error'/>
              </ListItemIcon>

              <Typography variant='body2' color='error'>
                {t("Cancel")}
              </Typography>
            </MenuItem>
        )}
      </Menu>

      <MessageBox
        bookAdmission
        onClose={closeModal}
        open={!!isModalOpen}
        title={t("Confirmation")}
        onAccept={onAcceptConfirmation}
        messageObject={t(confirmationModalMessage)}
        haveAccept={{ yes: t("Submit"), no: t("Close") }}
      />
    </>
  );
};

WidgetPatientToBeSeenContext.defaultProps = {
  ptInWard: null,
  noReviewTimer: false,
  noReviewReason: false,
  noAppointmentStatus: false,
};

WidgetPatientToBeSeenContext.propTypes = {
  noReviewTimer: PropTypes.bool,
  noReviewReason: PropTypes.bool,
  noAppointmentStatus: PropTypes.bool,
  ptInWard: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
};

export default WidgetPatientToBeSeenContext;
